import {
  getCarrierAmount,
  getCustomerAmount,
  getOrderTimelineForCustomer,
  isInternalShipment,
  isOrderFieldEditable,
} from '@hero/core';
import type {
  Amount,
  Currency,
  Order,
  OrderContractElement,
  OrderOverallStatus,
  VehicleTypes,
} from '@hero/schema';

/** Legacy layout hint for drawer/card visibility — does not drive priceUi. */
export type OrderDisplayForUser = 'customer' | 'carrier';

export type ComputeOrderDisplayInfoConfig = {
  order: Order;
  /**
   * Legacy layout hint (who sees carrier vs customer chrome). Price
   * display + edit rules are driven by `viewerLeg`, not this flag.
   */
  displayForUser: OrderDisplayForUser;
  /** Carrier driver app — price surfaces stay hidden. */
  isDriver?: boolean;
  /**
   * The viewer's contract leg (`useViewerLeg(order)`). Canonical source
   * for "my price" in the network model — buyer OR seller on that leg.
   */
  viewerLeg?: OrderContractElement;
  /**
   * True when the current user/org is `Order.shipper`. Walk-in
   * (no-client) orders have no viewer leg yet — the shipper still
   * needs an Add-price entry that routes through client assignment.
   */
  isViewerShipper?: boolean;
};

export type OrderDisplayPriceUi = {
  isInternalShipment: boolean;
  /** Whether the price card / breakdown should render at all. */
  showSection: boolean;
  /** Whether the viewer leg carries a non-zero price. */
  hasPrice: boolean;
  /**
   * Status gate + viewer leg + not internal. Matches what the price-edit
   * sheet can actually write (`useOrderPriceEditManager` → viewer leg).
   * Also true for the shipper on a walk-in (no client) so Add Price can
   * open the client-first flow.
   */
  canEdit: boolean;
  /** Open the price editor to set an initial price (`hasPrice === false`). */
  canAdd: boolean;
  /**
   * Walk-in / no external client — caller must assign a CRM client
   * before the price drawer/sheet can write a contract leg.
   */
  needsClientBeforePrice: boolean;
  /** Amount on the viewer leg, when present. */
  amount?: Amount;
  currency: Currency;
  viewerLeg?: OrderContractElement;
};

export type ComputedOrderDisplayInfo = ReturnType<typeof computeOrderDisplayInfo>;

/** Shared predicate for "this amount block represents a priced load". */
export const orderAmountHasPrice = (amount: Amount | undefined): boolean => {
  if (!amount) return false;
  return (
    (amount.itemsOriginalSubtotal ?? 0) > 0 ||
    (amount.subtotal ?? 0) > 0 ||
    (amount.total ?? 0) > 0
  );
};

/**
 * Price editability for the viewer's leg. `customer_price` and
 * `carrier_price` share the same status gate today — either key works.
 */
const isViewerLegPriceEditable = (orderStatus: OrderOverallStatus): boolean =>
  isOrderFieldEditable('carrier_price', orderStatus);

/**
 * Pure order display + editability selector — single source of truth
 * for drawer/card/mobile surfaces. Mirrors the `computeInvoiceInfo`
 * pattern in `useInvoiceInfo`.
 *
 * **Price rules are leg-centric:** `viewerLeg` drives amount, hasPrice,
 * showSection (when the viewer is a contract party), and canEdit.
 * `displayForUser` only feeds non-price layout visibility (sender info, etc.).
 */
export function computeOrderDisplayInfo(config: ComputeOrderDisplayInfoConfig) {
  const {
    order,
    displayForUser,
    isDriver = false,
    viewerLeg,
    isViewerShipper = false,
  } = config;

  const currency =
    getCustomerAmount(order).currency || getCarrierAmount(order).currency || 'SAR';

  const pickup = order.destinations?.find(d => d.type === 'pickup');
  const dropoff = order.destinations?.find(d => d.type === 'dropoff');
  const vehicleType: VehicleTypes | undefined =
    Array.isArray(order.load?.vehicles) && order.load.vehicles.length > 0
      ? (order.load.vehicles[0] as VehicleTypes)
      : undefined;
  const orderStatus = (order.status?.order || 'new') as OrderOverallStatus;
  const timeline = getOrderTimelineForCustomer(order);

  const extractAmount = (amount: Amount) => ({
    currency: amount.currency as Currency,
    total: amount.total,
    subtotal: amount.subtotal,
    shippingAmount: amount.itemsSubtotal,
    taxTotal: amount.tax.total,
    paid: amount.paid,
    remaining: amount.remaining,
    feesTotal: amount.fees.total,
  });

  // Legacy role-pricing block — used by web summary fallbacks when no
  // viewer leg exists yet. Not the canonical "my price" source.
  const roleAmount =
    displayForUser === 'carrier' ? getCarrierAmount(order) : getCustomerAmount(order);
  const pricing = extractAmount(roleAmount);

  const isInternal = isInternalShipment(order);
  const legAmount = viewerLeg?.amount;
  const hasPrice = orderAmountHasPrice(legAmount);
  const hasViewerLeg = !!viewerLeg;
  const priceStatusOk = isViewerLegPriceEditable(orderStatus);
  const needsClientBeforePrice =
    isInternal && isViewerShipper && !isDriver && priceStatusOk;
  const showPriceSection =
    (!isInternal && !isDriver && hasViewerLeg) || needsClientBeforePrice;
  const canEditPrice =
    (hasViewerLeg && !isInternal && priceStatusOk) || needsClientBeforePrice;

  const priceUi: OrderDisplayPriceUi = {
    isInternalShipment: isInternal,
    showSection: showPriceSection,
    hasPrice,
    canEdit: canEditPrice,
    canAdd: canEditPrice && showPriceSection && !hasPrice,
    needsClientBeforePrice,
    amount: legAmount,
    currency: legAmount?.currency ?? currency,
    viewerLeg,
  };

  const isCarrier = displayForUser === 'carrier';
  const isCustomer = displayForUser === 'customer';

  return {
    orderInfo: {
      currency,
      ref: order.ref,
      carrier: order.carrier,
      customer: order.shipper,
      sender: order.sender,
      receiver: order.receiver,
      driver: order.driver,
      vehicle: order.vehicle,
      pickup,
      dropoff,
      vehicleType,
      status: orderStatus,
      timeline,
      displayForUser,
    },
    pricing,
    status: {
      currentStatus: orderStatus,
      isNew: orderStatus === 'new',
      isAccepted: orderStatus === 'accepted',
      isConfirmed: orderStatus === 'confirmed',
      isInTransitPickup: orderStatus === 'in_transit_pickup',
      isArrivedPickup: orderStatus === 'arrived_pickup',
      isInTransitDropoff: orderStatus === 'in_transit_dropoff',
      isArrivedDropoff: orderStatus === 'arrived_dropoff',
      isDelivered: orderStatus === 'delivered',
      isCanceled: orderStatus === 'canceled',
    },
    timeline: {
      formatted: timeline,
      orderCreated: timeline.orderCreated,
      arrivedAtPickup: timeline.arrivedAtPickup,
      pickedUp: timeline.pickedUp,
      arrivedAtDropoff: timeline.arrivedAtDropoff,
      dropOff: timeline.dropOff,
      completed: timeline.completed,
    },
    editability: {
      canEditDestination: isOrderFieldEditable('destination', orderStatus),
      canEditOrderDates: isOrderFieldEditable('order_dates', orderStatus),
      canEditVehicle: isOrderFieldEditable('vehicle', orderStatus),
      canEditDriver: isOrderFieldEditable('driver', orderStatus),
      canEditCarrier: isOrderFieldEditable('carrier', orderStatus),
      canEditPrice: canEditPrice,
      canEditPODImages: isOrderFieldEditable('pod_images', orderStatus),
      canEditPODStatus: isOrderFieldEditable('pod_status', orderStatus),
      isCompleted: orderStatus === 'delivered' || orderStatus === 'canceled',
    },
    visibility: {
      showDriverInfo: true,
      showCustomerInfo: isCarrier,
      showSenderInfo: isCarrier,
      /** Viewer-leg price surfaces — same gate as `priceUi.showSection`. */
      showPrice: showPriceSection,
      showOrderAgainButton: isCustomer || isCarrier,
      showCarrierInfo: isCustomer,
    },
    priceUi,
  };
}
